/**
 * UnlockHint — empty-state that says WHY a widget is empty and what unlocks it.
 *
 * Day-1 dashboards used to render silent blanks: a widget with no data showed
 * nothing, and the user had no idea whether it was broken, loading, or waiting
 * on an integration. This component checks whether the data source the widget
 * needs is VISIBLE: connected AND synced (the coverage rule, lib/coverage.ts):
 *   - not visible → "Connect X to unlock this" with a deep-link CTA
 *   - visible     → quiet "no data yet" fallback (seen, and genuinely empty)
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getIntegrationCoverage } from '../../lib/integrations';
import type { SourceCoverage } from '@waypoint/shared';

export type UnlockNeed = 'crm' | 'billing' | 'email' | 'meetings';

/** Which coverage domain answers each need. */
const NEED_DOMAIN: Record<UnlockNeed, keyof SourceCoverage> = { crm: 'crm', billing: 'money', email: 'mailbox', meetings: 'contact' };

const NEED_LABELS: Record<UnlockNeed, string> = {
  crm: 'Connect your CRM (HubSpot, Close, or Salesforce)',
  billing: 'Connect billing (QuickBooks, Stripe, or Xero)',
  email: 'Connect Gmail or Outlook',
  meetings: 'Connect your calendar or Fireflies',
};

export interface UnlockHintProps {
  need: UnlockNeed;
  /** What the user gets, e.g. "to see which deals are at risk". */
  unlocks: string;
  /** Shown when the source IS visible (connected and synced) but no rows exist yet. */
  connectedFallback?: string;
}

export function UnlockHint({ need, unlocks, connectedFallback }: UnlockHintProps) {
  const { data: coverage } = useQuery({
    queryKey: ['integrations', 'coverage'],
    queryFn: getIntegrationCoverage,
    staleTime: 60_000,
  });

  // Visible = connected AND synced. A connected source that has not synced shows nothing, so it is not "empty".
  const connected = coverage?.[NEED_DOMAIN[need]] === true;

  if (connected) {
    return (
      <p className="text-2xs text-ink-dim italic">
        {connectedFallback ?? 'Nothing here yet — your data is still warming up.'}
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-dashed border-border bg-surface-1/50 px-3 py-3 text-center">
      <p className="text-2xs text-ink-dim">
        {NEED_LABELS[need]} {unlocks}
      </p>
      <Link
        to="/dashboard/settings?tab=integrations"
        className="mt-1.5 inline-block rounded-md bg-primary/10 px-2.5 py-1 text-2xs font-semibold text-primary hover:bg-primary/20"
      >
        Connect now →
      </Link>
    </div>
  );
}
